import { openingSideOffset2 } from "building-common/openings/openingQuery";
import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { GeneratedDoorPlacement, DoorDescriptor } from "building-types/doors/doorTypes";
import type { OpeningSide, OpeningShapes, OpeningSideContext } from "building-types/openings/openingTypes";
import type { ProfilesWithDepth } from "building-types/profiles/profileTypes";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type {
  HeadCasingArchedSlices,
  HeadCasingEyebrowTrim,
  HeadCasingFullArch,
  HeadCasingJackArch,
  HeadCasingSlices,
  HeadCasingStone,
  HeadCasingTrim,
} from "building-types/settings/headCasingSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import { HeadCasingType, SideCasingType } from "building-types/settings/settingsTypesEnum";
import uid from "common/utils/uid";
import { isExteriorDoorCasingSettings } from "doc/settingsTools";
import { DOOR_TRIM_DEPTH, DOOR_TRIM_WIDTH } from "doc/utils/doorTools";
import { arc2ByCenterStartAngle } from "geom/arc";
import { DIST_EPSILON } from "geom/core";
import { polyLine2ThickenedEdge } from "geom/offset";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2AABB2,
  polyLine2ConcatAll,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeLength,
  polyLine2EdgeStart,
  polyLine2Extend,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2ReverseCopy,
  polyLine2ScaleYAndKeepOrigin,
  polyLine2SetCCW,
  polyLine2Shift,
  polyLine2Simplify,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLineEdgeType } from "geom/polyLineTypes";
import type { Rect2 } from "geom/rect2Types";
import { vec2Lerp, vec2Dist } from "geom/vec2";
import {
  headCasingArchedSlicesProfiles,
  headCasingEyebrowTrimProfiles,
  headCasingFullArchProfiles,
  headCasingJackArchProfiles,
  headCasingStoneProfile,
  headCasingTrimProfile,
  headCasingSidingProfiles,
  sideCasingProfile,
} from "../casing/casingProfiles";
import { apronCasingEnabled, headCasingEnabled, sideCasingEnabled } from "../casing/casingQuery";
import { makeOpeningSide, openingProfileFromSections } from "../openings/openingProfile";
import { unionedProfiles } from "../openings/openingUtils";
import { checkOrientationWithMode } from "../streetOrientation";
import { mirrorProfiles } from "../walls/openingProfiles";
import { doorDescriptorHasCasing } from "./doorDescriptorQuery";
import { doorHasCasing } from "./doorQuery";
import { getArcCenter } from "./doorUtils";

function profileEdgeTypes(profile: PolyLine2, edgeType: PolyLineEdgeType): readonly PolyLineEdgeType[] {
  return Array<PolyLineEdgeType>(polyLine2EdgeCount(profile)).fill(edgeType);
}

export function makeDoorSide(
  context: OpeningSideContext,
  door: GeneratedDoorPlacement,
  casingSettings: ExteriorDoorCasingSettings | InteriorDoorCasingSettings | null,
  profileRectangular: OpeningSide,
  doorPlane: Plane,
  mirror: boolean,
): OpeningSide {
  const { opening2: rectangularOpening } = profileRectangular;

  // TODO(Andrew): how should this be calculated?
  const floorOffset = 0;

  const { descriptor, streetOrientation } = door;

  // @todo(Andrew): combine this with the context somehow; it's useful here as a type guard
  const isExterior = casingSettings && isExteriorDoorCasingSettings(casingSettings);

  const shapes = doorShapes(descriptor, rectangularOpening);
  let side = makeOpeningSide({
    openingRect2: rectangularOpening,
    opening2: shapes.outline,
    opening2EdgeTypes: shapes.outlineEdgeTypes,
    shapes,
    hasCasing: casingSettings ? doorHasCasing(door, casingSettings) : false,
    context,
  });

  if (mirror) {
    side = mirrorProfiles(side);
  }

  if (!doorDescriptorHasCasing(descriptor)) {
    return side;
  }

  if (!isExterior) {
    // @TODO(Andrew): calculate actual interior casing profiles here
    side.openingCasing2 = polyLine2SetCCW(polyLine2Copy(side.opening2));
    const left = polyLine2Edge(side.opening2, -1);
    const right = polyLine2Edge(side.opening2, 1);
    const top = polyLine2Extend(polyLine2Edge(side.opening2, 2), DOOR_TRIM_WIDTH, DOOR_TRIM_WIDTH);
    side.trim.push(
      {
        id: uid(),
        type: "SIDE_CASING",
        name: "trim.side",
        rail: left,
        elevationProfile: [polyLine2ThickenedEdge(left, 0, DOOR_TRIM_WIDTH)],
        width: DOOR_TRIM_WIDTH,
        depth: DOOR_TRIM_DEPTH,
      },
      {
        id: uid(),
        type: "SIDE_CASING",
        name: "trim.side",
        rail: right,
        elevationProfile: [polyLine2ThickenedEdge(right, 0, DOOR_TRIM_WIDTH)],
        width: DOOR_TRIM_WIDTH,
        depth: DOOR_TRIM_DEPTH,
      },
      {
        id: uid(),
        type: "HEAD_CASING",
        name: "trim.head",
        rail: top,
        elevationProfile: [polyLine2ThickenedEdge(top, 0, DOOR_TRIM_WIDTH)],
        width: DOOR_TRIM_WIDTH,
        depth: DOOR_TRIM_DEPTH,
      },
    );
    return side;
  }

  const {
    sideCasingType,
    apronCasingType,
    headCasingType,
    sideCasingTopOffset = 0,
    sideCasingDepth = 0,
    sideCasingBottomOffset = 0,
    casingHorizontalOffset = 0,
    casingOrientation = StreetOrientationMode.ALL_FACES,
  } = casingSettings;

  if (!checkOrientationWithMode(streetOrientation, casingOrientation)) {
    return side;
  }

  const outline2ForCasing = polyLine2Copy(openingSideOffset2(side, -casingHorizontalOffset));

  if (sideCasingEnabled(sideCasingType)) {
    const leftEdge = polyLine2Edge(outline2ForCasing, -1);
    const leftProfile = sideCasingProfile(
      leftEdge,
      casingSettings.sideCasingWidth as number,
      -floorOffset - sideCasingBottomOffset,
      sideCasingTopOffset,
    );
    side.sideCasing?.push(leftProfile);
    side.trim.push({
      id: uid(),
      type: "SIDE_CASING",
      name: "left",
      rail: leftEdge,
      width: casingSettings.sideCasingWidth,
      depth: sideCasingDepth,
      elevationProfile: leftProfile,
    });
    const rightEdge = polyLine2Edge(outline2ForCasing, 1);
    const rightProfile = sideCasingProfile(
      rightEdge,
      casingSettings.sideCasingWidth as number,
      sideCasingTopOffset,
      -floorOffset - sideCasingBottomOffset,
    );
    side.sideCasing?.push(rightProfile);
    side.trim.push({
      id: uid(),
      type: "SIDE_CASING",
      name: "right",
      width: casingSettings.sideCasingWidth,
      depth: sideCasingDepth,
      rail: rightEdge,
      elevationProfile: rightProfile,
    });
  }

  if (apronCasingEnabled(apronCasingType)) {
    const {
      apronHeight = 0,
      apronExtension = 0,
      apronDepth = 0,
      sideCasingType,
      sideCasingWidth = 0,
    } = casingSettings as ExteriorDoorCasingSettings;
    const apronLine = polyLine2Edge(outline2ForCasing, 0);
    const trimExtend = sideCasingWidth;
    side.apronCasing = doorApronCasingProfile(
      apronLine,
      apronHeight,
      sideCasingType !== SideCasingType.DISABLED ? trimExtend + apronExtension : apronExtension,
    );
    side.trim.push({
      id: uid(),
      type: "APRON",
      name: "apron",
      rail: apronLine,
      width: apronHeight,
      depth: apronDepth,
      elevationProfile: [side.apronCasing],
    });
  }

  if (headCasingEnabled(headCasingType)) {
    let headcasingLine = shapes.arcCurve ?? shapes.arcBar ?? polyLine2Edge(outline2ForCasing, 2);

    let profilesWithDepth: ProfilesWithDepth | undefined;
    if (sideCasingEnabled(sideCasingType) && headCasingType !== HeadCasingType.FULL_ARCH) {
      headcasingLine = polyLine2ExtendEnd(headcasingLine, casingSettings.sideCasingWidth);
      headcasingLine = polyLine2ExtendStart(headcasingLine, casingSettings.sideCasingWidth);
    }
    switch (headCasingType) {
      case HeadCasingType.EYEBROW_TRIM:
        profilesWithDepth = headCasingEyebrowTrimProfiles(
          polyLine2ReverseCopy(headcasingLine),
          casingSettings as HeadCasingEyebrowTrim,
        );
        break;
      case HeadCasingType.ARCHED_SLICES:
        profilesWithDepth = headCasingArchedSlicesProfiles(
          polyLine2ReverseCopy(headcasingLine),
          casingSettings as HeadCasingArchedSlices,
        );
        break;
      case HeadCasingType.FULL_ARCH:
        // NOTE(Andrew): I do not know where this magic number comes from; I am
        // preserving it here for posterity, but please note my extreme displeasure.
        const magicAdjust = -1;

        profilesWithDepth = headCasingFullArchProfiles(
          polyLine2ReverseCopy(headcasingLine),
          profileRectangular.opening2,
          casingSettings as HeadCasingFullArch,
          -(casingSettings.headCasingHeight + magicAdjust),
        );
        break;
      case HeadCasingType.TRIM:
        profilesWithDepth = headCasingTrimProfile(
          headcasingLine,
          shapes.arcCurve,
          casingSettings as HeadCasingTrim,
          shapes.arcCurveEdgeTypes,
        );
        break;
      case HeadCasingType.STONE:
        const stoneProfile = headCasingStoneProfile(headcasingLine, casingSettings as HeadCasingStone);
        side.headCasing.push(stoneProfile);
        side.trim.push({
          id: uid(),
          type: "HEAD_CASING",
          name: "stone",
          rail: headcasingLine,
          elevationProfile: [stoneProfile],
        });
        break;
      case HeadCasingType.JACK_ARCH:
        profilesWithDepth = headCasingJackArchProfiles(
          polyLine2ReverseCopy(headcasingLine),
          doorPlane,
          casingSettings as HeadCasingJackArch,
        );
        break;
      case HeadCasingType.SLICES:
        profilesWithDepth = headCasingSidingProfiles(
          polyLine2ReverseCopy(headcasingLine),
          casingSettings as HeadCasingSlices,
        );
        break;
      default:
        break;
    }

    if (profilesWithDepth) {
      side.headHeight = profilesWithDepth.depth;
      for (const profile of profilesWithDepth.profiles) {
        side.headCasing.push(profile);
      }
      side.trim.push(...profilesWithDepth.trim);
    }
  }

  const unioned = unionedProfiles(side, casingSettings);
  if (unioned.length > 0) {
    side.openingCasing2 = polyLine2SetCCW(unioned[0]);
  }

  return side;
}

export function doorShapes(descriptor: DoorDescriptor, outline: Rect2): OpeningShapes {
  switch (descriptor.operationType) {
    case DoorOperationType.PORTICO:
    case DoorOperationType.ARC:
      return arcDoorShapes(descriptor, outline);
    default:
      return rectangleDoorShapes(outline);
  }
}

function arcDoorShapes(descriptor: DoorDescriptor, outlineRectangular: Rect2): OpeningShapes {
  const { segmentedArc: segmentedArch, arcDepth } = descriptor;
  let { arcFactor = 1 / 3 } = descriptor;

  arcFactor = Math.min(Math.max(arcFactor, 0.0), 1.0);

  const width = polyLine2EdgeLength(outlineRectangular, 2);
  const topBox = polyLine2ThickenedEdge(outlineRectangular, 2, -width * arcFactor);
  polyLine2Shift(topBox, 2);
  const leftEdge = polyLine2Edge(topBox, 3);
  const topEdge = polyLine2Edge(topBox, 2);
  const rightEdge = polyLine2Edge(topBox, 1);
  const arcBar = polyLine2Edge(topBox, 0);
  let arcHeight = polyLine2EdgeLength(topBox, 3) * 0.5;

  if (arcDepth !== 0) {
    arcHeight = arcDepth;
  }

  let scaleY = polyLine2EdgeLength(leftEdge, 0) / width;
  let rads = Math.PI;
  let offsetY = 0;
  let cx = 0;
  let cy = 0;

  let arcInfillOutline: PolyLine2 | undefined = undefined;
  let arcCurve: PolyLine2 = arcBar;
  let arcIsCurved = false;

  if (arcFactor > DIST_EPSILON || arcHeight !== 0) {
    arcIsCurved = true;
    const [arcX, arcY] = vec2Lerp(arcBar[0], arcBar[1], arcBar[2], arcBar[3], 0.5);

    if (segmentedArch) {
      const srcRadius = vec2Dist(arcBar[0], arcBar[1], arcBar[2], arcBar[3]) * 0.5;

      [cx, cy] = getArcCenter(arcBar[0], arcBar[1], arcX, arcY + arcHeight, arcBar[2], arcBar[3]);
      offsetY = arcY - cy;

      scaleY = 1;

      const halfRads = Math.atan(srcRadius / offsetY);
      rads = 2 * halfRads;
    }

    arcCurve = arc2ByCenterStartAngle(arcX, arcY - offsetY, arcBar[0], arcBar[1], rads, 0.01, 10, 15);
    polyLine2ScaleYAndKeepOrigin(arcCurve, scaleY);
    const arcAABB = polyLine2AABB2(arcCurve);
    const arcShift = polyLine2EdgeLength(leftEdge, 0) - (arcAABB.maxY - arcAABB.minY);
    polyLine2Move(arcCurve, 0, arcShift - DIST_EPSILON);
    arcInfillOutline = polyLine2Simplify(polyLine2ConcatAll([rightEdge, topEdge, leftEdge, arcCurve], true));
  }

  const [rightBottomX, rightBottomY] = polyLine2EdgeStart(outlineRectangular, 1);
  const [leftBottomX, leftBottomY] = polyLine2EdgeEnd(outlineRectangular, 3);
  const arcEndOffset = arcCurve.length - 2;
  const { outline, outlineEdgeTypes } = openingProfileFromSections([
    {
      polyLine: polyLine2New([leftBottomX, leftBottomY, rightBottomX, rightBottomY]),
      edgeType: "STRAIGHT",
    },
    {
      polyLine: polyLine2New([rightBottomX, rightBottomY, arcCurve[0], arcCurve[1]]),
      edgeType: "STRAIGHT",
    },
    { polyLine: arcCurve, edgeType: arcIsCurved ? "CURVED" : "STRAIGHT" },
    {
      polyLine: polyLine2New([arcCurve[arcEndOffset], arcCurve[arcEndOffset + 1], leftBottomX, leftBottomY]),
      edgeType: "STRAIGHT",
    },
  ]);

  const outlineReversed = polyLine2Reverse(polyLine2Copy(outline));

  return {
    outline,
    outlineEdgeTypes,
    outlineReversed,
    outlineRectangular,
    arcOutline: arcInfillOutline,
    arcHeight,
    arcAngleRads: rads,
    arcCenterX: cx,
    arcCenterY: cy,
    arcRadius: 0,
    arcCurve,
    arcCurveEdgeTypes: profileEdgeTypes(arcCurve, arcIsCurved ? "CURVED" : "STRAIGHT"),
    arcFactor,
    arcBar,
  };
}

function rectangleDoorShapes(outlineRectangular: Rect2): OpeningShapes {
  const arcRadius = 0;
  const arcAngleRads = 0;
  const arcCenterX = 0;
  const arcCenterY = 0;
  const arcHeight = 0;
  const arcFactor = 0;

  const outlineReversed = polyLine2Reverse(polyLine2Copy(outlineRectangular));
  const { outline, outlineEdgeTypes } = openingProfileFromSections([
    { polyLine: outlineRectangular, edgeType: "STRAIGHT" },
  ]);

  return {
    outlineReversed: outlineReversed,
    outline,
    outlineEdgeTypes,
    outlineRectangular: outlineRectangular,
    arcHeight,
    arcAngleRads,
    arcCenterX,
    arcCenterY,
    arcRadius,
    arcFactor,
  };
}

export function doorApronCasingProfile(apronLine: PolyLine2, apronHeight: number, trimExtend?: number): PolyLine2 {
  if (trimExtend !== undefined) {
    polyLine2ExtendEnd(apronLine, trimExtend);
    polyLine2ExtendStart(apronLine, trimExtend);
  }
  return polyLine2ThickenedEdge(apronLine, 0, apronHeight);
}

export function doorSideCasingProfile({
  edge,
  casingWidth,
  extendStart,
  extendEnd,
}: {
  edge: PolyLine2;
  casingWidth: number;
  extendStart?: number;
  extendEnd?: number;
}): PolyLine2 {
  if (extendStart !== undefined) {
    polyLine2ExtendStart(edge, extendStart);
  }
  if (extendEnd !== undefined) {
    polyLine2ExtendEnd(edge, extendEnd);
  }
  return polyLine2ThickenedEdge(edge, 0, casingWidth);
}
